import { __assign, __rest } from "tslib";
import React, { useCallback, useEffect, useState, useMemo, } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Loader, Text } from '@wix/design-system';
import { Selector } from '../selector/Selector';
import { fetchUserAccounts, getLoggedInAccount } from '../services/api';
import { getUserInfoFromCookie } from '../services/utils';
import { withProviders } from '../selector/Selector.container';
import { reportWorkspaceSelected, reportWorkspaceSelectorLoaded, } from '../services/bi/bi.service';
import s from '../stories/styles/WorkspaceSelector.scss';
import { allowedDomainList, isValidRedirectUrl } from '../utils';
import { useExperiments } from '@wix/wix-experiments-react';
var getUrlParam = function (param) { return new URLSearchParams(location.search).get(param); };
var WorkspaceSelectorComp = function (_a) {
    var email = _a.email, title = _a.title, subTitle = _a.subTitle, actionBtnText = _a.actionBtnText, userAccounts = _a.userAccounts, onAccountActionClicked = _a.onAccountActionClicked, onError = _a.onError, onLoaded = _a.onLoaded, isMobile = _a.isMobile, dataHook = _a.dataHook, userId = _a.userId, locale = _a.locale, wsrProviderProps = _a.wsrProviderProps, props = __rest(_a, ["email", "title", "subTitle", "actionBtnText", "userAccounts", "onAccountActionClicked", "onError", "onLoaded", "isMobile", "dataHook", "userId", "locale", "wsrProviderProps"]);
    var t = useTranslation().t;
    var _b = useState(), userAccountsData = _b[0], setUserAccountsData = _b[1];
    var _c = useState(true), isLoading = _c[0], setIsLoading = _c[1];
    var _d = useState(''), loggedInAccountId = _d[0], setLoggedInAccountId = _d[1];
    var userIdMemo = useMemo(function () { var _a; return userId || ((_a = getUserInfoFromCookie()) === null || _a === void 0 ? void 0 : _a.userId); }, [
        userId,
    ]);
    var defaultRedirect = useMemo(function () {
        var _a;
        var hostName = (_a = location.hostname) === null || _a === void 0 ? void 0 : _a.split('.');
        if ((hostName === null || hostName === void 0 ? void 0 : hostName.length) === 3) {
            return "https://manage.".concat(hostName[1], ".").concat(hostName[2]);
        }
        return 'https://manage.wix.com';
    }, []);
    var experiments = useExperiments().experiments;
    var onAccountClicked = useCallback(function (account) {
        var _a, _b;
        var originInfo = (_a = getUrlParam('origin_info')) !== null && _a !== void 0 ? _a : '';
        reportWorkspaceSelected(account.accountId, originInfo);
        if (onAccountActionClicked) {
            return onAccountActionClicked(account);
        }
        var redirectTo = getUrlParam('redirect_to');
        if (experiments.enabled('specs.shouldWhitelistRedirect')) {
            var redirectUrl_1 = isValidRedirectUrl(redirectTo !== null && redirectTo !== void 0 ? redirectTo : '', allowedDomainList) ? redirectTo : defaultRedirect;
            location.href = "".concat(defaultRedirect, "/switch-account?account_id=").concat(account.accountId, "&redirect_to=").concat(redirectUrl_1);
            return;
        }
        var redirectUrl = encodeURIComponent((_b = getUrlParam('redirect_to')) !== null && _b !== void 0 ? _b : defaultRedirect);
        location.href = "".concat(defaultRedirect, "/switch-account?account_id=").concat(account.accountId, "&redirect_to=").concat(redirectUrl);
    }, []);
    useEffect(function () {
        var fetchAccounts = function () {
            return userAccounts
                ? Promise.resolve({ accounts: userAccounts })
                : fetchUserAccounts();
        };
        Promise.all([fetchAccounts(), getLoggedInAccount()])
            .then(function (_a) {
            var _b;
            var accountsResponse = _a[0], accountId = _a[1];
            if (!(accountsResponse === null || accountsResponse === void 0 ? void 0 : accountsResponse.accounts)) {
                return onError === null || onError === void 0 ? void 0 : onError('an error occurred');
            }
            var accountsData = accountsResponse.accounts.map(function (account) { return ({
                accountId: account.accountId,
                accountName: account.accountName,
                accountOwner: account.accountOwner,
                accountDisplayData: {
                    imageUrl: account.accountImg,
                    name: account.accountName,
                },
            }); });
            if (accountsData.length === 1) {
                var xssRegex = /(\b)(on\S+)(\s*)=|javascript|<(|\/|[^\/>][^>]+|\/[^>][^>]+)>/;
                var redirectUrl = getUrlParam('redirect_to');
                if (redirectUrl && !xssRegex.test(redirectUrl)) {
                    return (location.href = redirectUrl);
                }
                return (location.href = defaultRedirect);
            }
            else {
                var originInfo = (_b = getUrlParam('origin_info')) !== null && _b !== void 0 ? _b : '';
                setUserAccountsData(accountsData);
                reportWorkspaceSelectorLoaded(originInfo);
                onLoaded && onLoaded(accountsData);
            }
            setLoggedInAccountId(accountId);
            setIsLoading(false);
        })
            .catch(function (error) {
            setIsLoading(false);
            onError && onError(error);
        });
    }, []);
    var accountsData = userAccountsData === null || userAccountsData === void 0 ? void 0 : userAccountsData.map(function (account) { return (__assign(__assign({}, account), { accountDisplayData: __assign(__assign({}, account.accountDisplayData), { description: account.accountOwner && account.accountOwner === userIdMemo
                ? t('workspace_selector.owner')
                : '' }) })); });
    return isLoading ? (React.createElement(Box, { align: "center", direction: "vertical", verticalAlign: "middle", height: "100%", position: "absolute", bottom: "0", "margin-left": "auto", "margin-right": "auto", left: "0", right: "0" },
        React.createElement(Loader, null))) : (React.createElement(Selector, __assign({ userAccounts: accountsData, email: email, isLoading: isLoading, avatarShape: "square", title: title !== null && title !== void 0 ? title : t('workspace_selector.default_title'), subTitle: React.createElement(SubTitle, { email: email, text: subTitle, isMobile: !!isMobile }), onAccountActionClicked: onAccountClicked, isMobile: isMobile, loggedInUserData: {
            accountId: loggedInAccountId,
        } }, props)));
};
var SubTitle = function (_a) {
    var text = _a.text, email = _a.email, isMobile = _a.isMobile;
    if (React.isValidElement(text)) {
        return text;
    }
    return text ? (React.createElement(Text, { id: s["workspace_subtitile"], size: "small" }, text)) : (React.createElement(Text, { id: s["workspace_subtitile"], size: "small" },
        React.createElement(Trans, { i18nKey: "workspace_selector.default_sub_title", values: { email: email } },
            React.createElement("span", null, "You have multiple workspaces in Wix using "),
            isMobile && React.createElement("br", null),
            React.createElement("b", null, "email"),
            !isMobile && React.createElement("br", null),
            React.createElement("span", null, "Choose a workspace and continue"))));
};
export var WorkspaceSelector = withProviders(WorkspaceSelectorComp);
//# sourceMappingURL=WorkspaceSelector.js.map